import { arktypeResolver } from '@hookform/resolvers/arktype';
import { CircularProgress } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { QueryStatus } from '@reduxjs/toolkit/query';
import Levenshtein from 'levenshtein';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  calizaApi,
  useCreateContactMutation,
  useGetCountryStatesQuery,
  useLazyGetPlaceDetailsQuery,
} from '@/api/v1';
import { AddressSearchResultItem } from '@/components/molecules/AddressSearchResultItem';
import { Autocomplete } from '@/components/molecules/Autocomplete';
import { InputWithSearchDropdown } from '@/components/molecules/InputWithSearchDropdown';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OneOf } from '@/components/molecules/OneOf';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import countries from '@/data/countries';
import { ControllerWithError } from '@/forms/ControllerWithError';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { useCustomer } from '@/hooks/useCustomer';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { useStateCities } from '@/hooks/useStateCities';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { recipientSchema } from '@/schemas/recipient';
import { contactFormToContactDTO } from '@/Screens/Dashboard/Flows/TransferFlow/helpers';
import { AddressAutocompleteSuggestion } from '@/types/addressValidation';
import {
  BusinessRelationshipType,
  businessRelationshipTypeLabels,
  Contact,
  ContactType,
} from '@/types/contacts';
import { CountryType } from '@/types/country';
import { javaEnumToSelectItem, SelectItem } from '@/types/fields';
import { ModalFlowContext } from '@/types/modalFlowContext';
import { NamedIdentified } from '@/types/namedIdentified';
import { OneOfOption } from '@/types/oneOfOption';
import { ScoredItem } from '@/types/scoredItem';
import { normalizeString } from '@/utility/normalizeString';

type ContactForm = typeof recipientSchema.infer;

export const RecipientDetailsModal: React.FC = (): React.ReactElement => {
  const toast = useToast();

  const [createContact, mutation] = useCreateContactMutation();
  const [streetSearchKeyword, setStreetSearchKeyword] = React.useState<string>('');

  const customer = useCustomer();
  const modal = useOutletContext<ModalFlowContext>();
  const dispatch = useDispatch<any>();
  const tr = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();

  const { register, handleSubmit, watch, setValue, setError, control } =
    useFormWithErrors<ContactForm>({
      defaultValues: defaultValues,
      resolver: arktypeResolver(recipientSchema),
    });

  const handleSuccess = React.useCallback(
    (contact: Contact): void => {
      dispatch(calizaApi.util.invalidateTags(['Contacts']));
      navigate(`../contact/${contact.id}/payment-method${location.search}`);
    },
    [dispatch, location.search, navigate],
  );

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.fulfilled) {
      handleSuccess(mutation.data);
    }
    if (mutation.status === QueryStatus.rejected) {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    }
  }, [handleSuccess, mutation.data, mutation.status, toast, tr]);

  const onClose = React.useCallback((): void => {
    modal.close();
  }, [modal]);

  const [type, country, state, city] = watch(['type', 'country', 'address.state', 'address.city']);
  const [getPlaceDetails, getPlaceDetailsStatus] = useLazyGetPlaceDetailsQuery();

  const {
    data: states = [],
    isLoading: statesLoading,
    isError: statesError,
    refetch: refetchStates,
  } = useGetCountryStatesQuery(country, { skip: !country });
  const hasStates = states.length > 0;

  const onSubmit = React.useCallback(
    (form: ContactForm): void => {
      if (!customer.beneficiaryId) {
        console.error('error: beneficiaryId is not set');
        return;
      }

      if (hasStates && !form.address.state) {
        setError('address.state', { type: 'manual', message: tr('VALIDATION_REQUIRED') });
        return;
      }

      createContact(contactFormToContactDTO(customer.beneficiaryId, form));
    },
    [customer.beneficiaryId, createContact, hasStates, setError, tr],
  );
  const stateCities = useStateCities(country, state);
  const cities = React.useMemo(
    (): readonly string[] =>
      stateCities
        .map((item: NamedIdentified): string => item.name)
        .filter((item: string): boolean => item.toLowerCase().includes(city.toLowerCase())),
    [city, stateCities],
  );
  const contactTypeOptions = React.useMemo((): readonly OneOfOption[] => {
    return contactTypeEitherOption(tr);
  }, [tr]);

  const [addressSearchResults, addressSearchRunning] = useAddressSearch(
    country,
    streetSearchKeyword,
  );

  const handleStreetKeywordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.currentTarget;
      setStreetSearchKeyword(value);
    },
    [],
  );

  const handleAddressSelected = React.useCallback(
    async (suggestion: AddressAutocompleteSuggestion): Promise<void> => {
      const {
        data: place = {
          streetAddress: '',
          postalCode: '',
          city: '',
          state: '',
          stateCode: '',
          country: '',
          countryCode: '',
          placeId: '',
          types: [],
        },
      } = await getPlaceDetails(suggestion.placeId);

      const findState = stateMatcher(place.state, place.stateCode);
      const stateId = hasStates ? findState(states) : undefined;

      setValue('address', {
        street: place.streetAddress,
        state: stateId?.id ?? null,
        complement: '',
        zipCode: place.postalCode,
        city: place.city ?? '',
      });
    },
    [getPlaceDetails, hasStates, setValue, states],
  );

  const streetAddressEndAdornment = React.useMemo(
    (): React.ReactElement | undefined =>
      getPlaceDetailsStatus.status === QueryStatus.pending || addressSearchRunning ? (
        <InputAdornment position="end">
          <CircularProgress size={22} thickness={7} color="inherit" />
        </InputAdornment>
      ) : undefined,
    [addressSearchRunning, getPlaceDetailsStatus.status],
  );

  const resetAddressComponents = React.useCallback((): void => {
    setValue('address', {
      street: '',
      state: null,
      complement: '',
      zipCode: '',
      city: '',
    });
  }, [setValue]);

  React.useEffect((): void => {
    if (streetSearchKeyword.trim() === '') {
      resetAddressComponents();
    }
  }, [resetAddressComponents, streetSearchKeyword]);

  React.useEffect(resetAddressComponents, [resetAddressComponents, country]);

  React.useEffect((): void => {
    setValue('address.state', hasStates ? '' : null);
  }, [hasStates, setValue]);

  return (
    <ApplicationModal title={tr('RECIPIENT_DETAILS')} back="" onClose={onClose}>
      <Box
        component="form"
        display="flex"
        flexDirection="column"
        gap={5}
        onSubmit={handleSubmit(onSubmit)}>
        <OneOf
          defaultValue={defaultValues.type}
          options={contactTypeOptions}
          {...register({ name: 'type', label: tr('RECIPIENT_TYPE') })}
        />

        <Box display="flex" flexDirection="column" gap={2}>
          <Typography fontSize={16} fontWeight={400} lineHeight="24px">
            {tr('INFORMATION_FOR_PAYMENT_BENEFICIARY_OR_RECIPIENT')}
          </Typography>

          {match<ContactType, React.ReactElement>(type)
            .with(
              ContactType.company,
              (): React.ReactElement => (
                <TextField
                  {...register({ name: 'legalEntityName', label: tr('LEGAL_ENTITY_NAME') })}
                />
              ),
            )
            .with(
              ContactType.individual,
              (): React.ReactElement => (
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <TextField
                    fullWidth={true}
                    {...register({ name: 'firstName', label: tr('FIRST_NAME') })}
                  />
                  <TextField
                    fullWidth={true}
                    {...register({ name: 'lastName', label: tr('LAST_NAME') })}
                  />
                </Box>
              ),
            )
            .exhaustive()}

          <TextField
            select={true}
            defaultValue={BusinessRelationshipType.unset}
            {...register({ name: 'businessRelationship', label: tr('BUSINESS_RELATIONSHIP') })}>
            {javaEnumToSelectItem(businessRelationshipTypeLabels).map(
              (item: SelectItem): React.ReactElement => (
                <MenuItem key={item.value} value={item.value}>
                  {tr(item.label)}
                </MenuItem>
              ),
            )}
          </TextField>

          <TextField
            select={true}
            defaultValue={CountryType.unset}
            {...register({ name: 'country', label: tr('COUNTRY_OF_RESIDENCE') })}>
            {countries.map(
              (country: SelectItem<CountryType>): React.ReactElement => (
                <MenuItem key={country.value} value={country.value}>
                  {tr(country.label)}
                </MenuItem>
              ),
            )}
          </TextField>

          {country === CountryType.unset ? (
            <></>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <InputWithSearchDropdown
                defaultValue=""
                fullWidth={true}
                results={addressSearchResults}
                itemComponent={AddressSearchResultItem}
                {...register({ name: 'address.street', label: tr('STREET_ADDRESS') })}
                endAdornment={streetAddressEndAdornment}
                onItemClick={handleAddressSelected}
                onChange={handleStreetKeywordChange}
              />

              <TextField
                defaultValue=""
                fullWidth={true}
                {...register({ name: 'address.complement', label: tr('SUITE_FLOOR_APT') })}
              />

              <Grid container={true} spacing={2} width="100%">
                {(hasStates || statesLoading) && (
                  <Grid size={12}>
                    <ControllerWithError
                      name="address.state"
                      label={tr('STATE')}
                      control={control}
                      render={({ field }: any): React.ReactElement => (
                        <TextField
                          select={!statesLoading}
                          fullWidth={true}
                          value={field.value}
                          error={field.error}
                          helperText={field.helperText}
                          label={field.label}
                          onChange={field.onChange}
                          disabled={statesLoading}
                          slotProps={{
                            input: {
                              endAdornment: statesLoading ? (
                                <InputAdornment position="end">
                                  <CircularProgress size={22} thickness={7} color="inherit" />
                                </InputAdornment>
                              ) : undefined,
                            },
                          }}>
                          {states.map((state: NamedIdentified): React.ReactElement => {
                            return (
                              <MenuItem key={state.id} value={state.id}>
                                {state.name}
                              </MenuItem>
                            );
                          })}
                          <MenuItem value="N/A">{tr('NOT_APPLICABLE')}</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                )}
                <Grid container={true} direction="row" size={12} spacing={2}>
                  <Grid size={6}>
                    <ControllerWithError
                      name="address.city"
                      label={tr('CITY')}
                      control={control}
                      render={({ field }: any): React.ReactElement =>
                        hasStates ? (
                          <Autocomplete
                            fullWidth={true}
                            items={cities}
                            label={field.label}
                            value={field.value}
                            error={field.error}
                            helperText={field.helperText}
                            onChange={field.onChange}
                          />
                        ) : (
                          <TextField
                            fullWidth={true}
                            value={field.value}
                            error={field.error}
                            helperText={field.helperText}
                            label={field.label}
                            onChange={field.onChange}
                          />
                        )
                      }
                    />
                  </Grid>
                  <Grid size={6}>
                    <ControllerWithError
                      name="address.zipCode"
                      label={tr('ZIP')}
                      control={control}
                      render={({ field }: any): React.ReactElement => (
                        <TextField
                          fullWidth={true}
                          value={field.value}
                          error={field.error}
                          helperText={field.helperText}
                          label={field.label}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}

          {statesError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={refetchStates}>
                  {tr('RETRY')}
                </Button>
              }>
              {tr('FAILED_TO_LOAD_STATES')}
            </Alert>
          )}

          <Box paddingTop={4}>
            <LoadingButton
              variant="dark"
              fullWidth={true}
              type="submit"
              disabled={statesLoading || statesError}
              loading={mutation.status === QueryStatus.pending}>
              {tr('NEXT')}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </ApplicationModal>
  );
};

const contactTypeEitherOption = (tr: (key: string) => string): readonly OneOfOption[] => [
  {
    label: tr('COMPANY'),
    value: ContactType.company,
  },
  {
    label: tr('INDIVIDUAL'),
    value: ContactType.individual,
  },
];

const defaultValues: ContactForm = {
  type: ContactType.individual,
  firstName: '',
  lastName: '',
  country: CountryType.unset,
  businessRelationship: BusinessRelationshipType.unset,
  address: {
    street: '',
    complement: '',
    city: '',
    state: null,
    zipCode: '',
  },
};

const cleanedUp = (value: string): string =>
  normalizeString(value)
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, '')
    .replace('Department', '')
    .replace('Departmento de', '')
    .replace('Departmento', '')
    .replace('Provincia de', '')
    .replace('Provincia', '')
    .replace('Province', '')
    .toLowerCase();

const getScore = (state: NamedIdentified, stateCode: string, stateName: string): number => {
  const { id, name } = state;
  const cleanedName = cleanedUp(name);
  const cleanedStateName = cleanedUp(stateName);
  // Highest score if IDs are equal (case-insensitive)
  if (id.toLowerCase() === stateCode.toLowerCase()) {
    return 100;
  } else if (cleanedName === cleanedStateName) {
    return 75;
  }
  // Fallback to Levenshtein distance for name matching
  else {
    // Calculate Levenshtein distance between cleaned-up names
    const distance = new Levenshtein(cleanedName, cleanedStateName).distance;

    // Convert distance to a score (lower distance = higher score)
    // Max length is used to normalize the score
    const maxLength = Math.max(name.length, stateName.length);
    const normalizedScore = maxLength > 0 ? (maxLength - distance) / maxLength : 0;

    // Scale to a 0-25 range (lower than the other matching methods)
    return normalizedScore * 25;
  }
};

const stateMatcher =
  (stateName: string, stateCode: string) =>
  (states: readonly NamedIdentified[]): NamedIdentified | undefined => {
    // Score each state and sort by score (highest first)
    const scoredStates = states.map((state: NamedIdentified): ScoredItem => {
      const score = getScore(state, stateCode, stateName);

      return { state, score };
    });

    // Sort by score (highest first)
    scoredStates.sort((a: ScoredItem, b: ScoredItem): number => b.score - a.score);

    // Return the state with the highest score if it has a meaningful score
    return scoredStates.length > 0 && scoredStates[0].score > 0 ? scoredStates[0].state : undefined;
  };
